import { cn } from "../lib/cn";

export interface DayLadderMedication {
  id: string;
  label: string;
  /** Hours past midnight (0–24, may be fractional) each dose falls at. */
  doseHours: number[];
  /** Marks the row as needing attention (renders in the magenta accent). */
  flagged?: boolean;
}

export interface DayLadderMarker {
  hour: number;
  label: string;
}

/**
 * A 24-hour dosing-schedule timeline — one row per medication, a tick per dose. New in the
 * Broadsheet redesign: makes a patient's actual pill burden and dose clustering visible at a
 * glance (e.g. "these four doses all land within an hour of each other"), which a plain list
 * of frequencies doesn't show. Dose hours are inferred from the medication's structured
 * `frequency` (see `inferDoseHoursFromFrequency`) since DOSA doesn't yet capture literal
 * dose-times — that inference, not the visualization, is the thing to revisit if this needs
 * to reflect a patient's actual customized schedule later.
 */
export function MedicationDayLadder({
  medications,
  markers = [],
  className,
}: {
  medications: DayLadderMedication[];
  markers?: DayLadderMarker[];
  className?: string;
}) {
  const pct = (hour: number) => `${(hour / 24) * 100}%`;

  return (
    <div dir="ltr" className={cn("min-w-[480px]", className)}>
      {markers.length > 0 && (
        <div className="mb-1.5 grid grid-cols-[172px_1fr] items-center">
          <div />
          <div className="relative h-5">
            {markers.map((m) => (
              <div key={m.label} className="absolute whitespace-nowrap text-[11px] text-clinical-high" style={{ left: pct(m.hour) }}>
                {m.label}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="relative">
        {markers.length > 0 && (
          <div className="pointer-events-none absolute bottom-[22px] start-[172px] end-0 top-0">
            {markers.map((m) => (
              <div
                key={m.label}
                className="absolute bottom-0 top-0 w-px"
                style={{
                  left: pct(m.hour),
                  backgroundImage: "repeating-linear-gradient(currentColor 0 4px, transparent 4px 8px)",
                }}
              >
                <span className="text-clinical-high">&nbsp;</span>
              </div>
            ))}
          </div>
        )}

        {medications.map((med) => (
          <div key={med.id} className="grid h-9 grid-cols-[172px_1fr] items-center">
            <div
              className={cn(
                "truncate pe-4 text-end text-[13.5px]",
                med.flagged ? "font-semibold text-clinical-high" : "text-charcoal-800/80",
              )}
            >
              {med.label}
            </div>
            <div className="relative h-9">
              {med.doseHours.map((hour, i) => (
                <div
                  key={i}
                  className={cn("absolute top-3 h-3 w-[30px] rounded-full", med.flagged ? "bg-clinical-high" : "bg-charcoal-900")}
                  style={{ left: pct(hour) }}
                />
              ))}
            </div>
          </div>
        ))}

        <div className="grid h-[22px] grid-cols-[172px_1fr]">
          <div />
          <div className="relative border-t border-charcoal-900/25 text-[11px] text-charcoal-500">
            <span className="absolute top-[5px] start-0">00:00</span>
            <span className="absolute top-[5px] start-1/3">08:00</span>
            <span className="absolute top-[5px] start-2/3">16:00</span>
            <span className="absolute top-[5px] end-0">24:00</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const FREQUENCY_DOSE_HOURS: Record<string, number[]> = {
  ONCE_DAILY: [8],
  TWICE_DAILY: [8, 20],
  THREE_TIMES_DAILY: [8, 14, 20],
  FOUR_TIMES_DAILY: [8, 12, 16, 20],
  EVERY_OTHER_DAY: [8],
  WEEKLY: [8],
  AS_NEEDED: [],
  OTHER: [8],
};

/** Approximate dose clock-times from a structured frequency value — see the component-level
 * note above on why this is inferred rather than patient-specified. */
export function inferDoseHoursFromFrequency(frequency: string | undefined): number[] {
  if (!frequency) return [8];
  return FREQUENCY_DOSE_HOURS[frequency] ?? [8];
}
